<template>
  <!-- The row stays exactly where it is; this region opens beneath it by
       animating grid-template-rows 0fr → 1fr. No measurement, no overlay,
       no body scroll lock, so the reader never loses their place. -->
  <div
    :id="id"
    ref="rootEl"
    class="dossier"
    :class="{ 'is-open': open }"
    :style="vars"
    role="region"
    :aria-labelledby="`${id}-title`"
    @keydown.esc="$emit('close')"
  >
    <div class="d-clip" :inert="!open || null" :aria-hidden="!open ? 'true' : null">
      <div class="d-inner">
        <!-- The dossier needs a heading to be a named region and a focus
             target on open. It does not need to repeat the title the reader
             is looking at twelve pixels above it, so it is read, not shown. -->
        <h3 :id="`${id}-title`" ref="headingEl" class="d-heading" tabindex="-1">
          {{ title }}
        </h3>

        <!-- allow-fade: an opacity-only state change, so §4.3's global
             reduced-motion clamp lets it keep 200ms and stay perceivable. -->
        <div class="d-content allow-fade">
          <div class="d-cols">
            <p class="d-desc">{{ description }}</p>

            <dl class="d-spec">
              <template v-for="row in specs" :key="row.key">
                <dt class="label">{{ row.label }}</dt>
                <dd class="num">{{ row.value }}</dd>
              </template>
              <template v-if="liveHref">
                <dt class="label">{{ $t("dossier.link") }}</dt>
                <dd class="num">
                  <a class="d-link" :href="liveHref" target="_blank" rel="noopener">
                    {{ linkLabel }}
                    <span class="sr-only"> {{ $t("a11y.newTab") }}</span>
                  </a>
                </dd>
              </template>
            </dl>
          </div>

          <!-- Evidence ladder. Behind v-if on ever-having-been-opened, so a
               cold page load never fetches project imagery; once fetched the
               nodes stay, so closing does not strip content mid-collapse. -->
          <div v-if="loaded" class="d-evidence">
            <!-- T1 — the live capture, at full width, no frame, no chrome. -->
            <template v-if="tier === 'T1'">
              <figure v-for="src in caps" :key="src" class="t1">
                <img
                  class="t1-plate"
                  :src="url(src)"
                  :alt="altFor(src)"
                  width="1800"
                  height="1125"
                  loading="lazy"
                  decoding="async"
                />
                <figcaption v-if="!altFor(src)" class="d-caption">{{ caption }}</figcaption>
              </figure>
            </template>

            <!-- T2 — a snap strip. The 20px mat is mixed from the project's
                 own colour so a portrait iPad shot and a landscape desktop
                 shot sit together without a white frame between them. -->
            <div
              v-else-if="tier === 'T2'"
              class="t2"
              tabindex="0"
              role="group"
              :aria-label="$t('dossier.shots')"
            >
              <figure v-for="src in shts" :key="src" class="t2-shot">
                <img
                  :src="url(src)"
                  :alt="altFor(src)"
                  width="300"
                  height="220"
                  loading="lazy"
                  decoding="async"
                />
                <figcaption v-if="!altFor(src)" class="d-caption">{{ caption }}</figcaption>
              </figure>
            </div>

            <!-- T3 — the mark, bare, on a bloom of its own colour. The ground
                 shadow is a radial-gradient element; filter: blur() is banned. -->
            <div v-else-if="tier === 'T3'" class="t3">
              <div class="t3-bloom" aria-hidden="true"></div>
              <div class="t3-stack">
                <ProjectMark :project="project" :size="markLarge" :alt="markAlt" />
                <div class="t3-shadow" aria-hidden="true"></div>
              </div>
            </div>

            <!-- T4 — a panel in the project's own gradient. Colour channel 2. -->
            <div v-else class="t4">
              <div ref="t4MarkEl" class="t4-mark">
                <ProjectMark :project="project" :size="markPanel" :alt="markAlt" />
              </div>
              <p class="t4-tech">{{ stack }}</p>
            </div>

            <div v-if="highlights.length" class="d-highlights">
              <p class="label">{{ $t("dossier.highlights") }}</p>
              <ul>
                <li v-for="line in highlights" :key="line">{{ line }}</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import ProjectMark from "@/components/project/ProjectMark.vue";
import { registerLayer, DEPTH } from "@/composables/useDepth";
import {
  assetUrl,
  captures,
  colourVars,
  gradientOf,
  shots,
  tierOf,
} from "@/composables/useProjectAssets";
import { useIsNarrow } from "@/composables/useMediaQuery";

export default {
  name: "ProjectDossier",
  components: { ProjectMark },
  props: {
    project: { type: Object, required: true },
    open: { type: Boolean, default: false },
    id: { type: String, required: true },
  },
  emits: ["close"],
  setup(props) {
    const { t, te, locale } = useI18n();
    const isNarrow = useIsNarrow();

    const pick = (field) => {
      if (!field) return "";
      if (typeof field === "string") return field;
      return field[locale.value] || field.en || "";
    };

    const title = computed(() => pick(props.project.title));
    const description = computed(() => pick(props.project.description));
    const caption = computed(() =>
      [title.value, pick(props.project.category)].filter(Boolean).join(" · ")
    );
    const markAlt = computed(() => `${title.value} logo`);

    /* Status is a slug. A missing key renders an empty value cell — the page
       never prints "status.undefined" at a visitor. */
    const statusLabel = computed(() => {
      const slug = props.project.status;
      if (!slug) return "";
      const key = `status.${slug}`;
      return te(key) ? t(key) : "";
    });

    const stack = computed(() => (props.project.technologies || []).join(" · "));

    const liveHref = computed(() => {
      const url = props.project.liveUrl;
      return url && url !== "#" ? url : "";
    });
    const linkLabel = computed(() => {
      if (!liveHref.value) return "";
      try {
        return new URL(liveHref.value).hostname.replace(/^www\./, "");
      } catch (e) {
        return liveHref.value;
      }
    });

    // A row is omitted entirely when the project has no value for it.
    const specs = computed(() => {
      const p = props.project;
      const rows = [];
      if (p.year) rows.push({ key: "year", label: t("dossier.year"), value: String(p.year) });
      const role = pick(p.role);
      if (role) rows.push({ key: "role", label: t("dossier.role"), value: role });
      if (p.status) rows.push({ key: "status", label: t("dossier.status"), value: statusLabel.value });
      if (p.client) rows.push({ key: "client", label: t("dossier.client"), value: p.client });
      if (stack.value) rows.push({ key: "stack", label: t("dossier.stack"), value: stack.value });
      return rows;
    });

    const tier = computed(() => tierOf(props.project));
    const caps = computed(() => captures(props.project));
    const shts = computed(() => shots(props.project));
    const highlights = computed(() => {
      const h = props.project.highlights;
      return (h && (h[locale.value] || h.en)) || [];
    });

    /* imageAlt is authored per shot and indexed against `images`. A missing
       entry falls back to an empty alt plus a visible caption, never a filename. */
    const altFor = (path) => {
      const i = (props.project.images || []).indexOf(path);
      const alt = props.project.imageAlt;
      const list = (alt && (alt[locale.value] || alt.en)) || [];
      return (i > -1 && list[i]) || "";
    };

    const markLarge = computed(() => (isNarrow.value ? 200 : 300));
    const markPanel = computed(() => (isNarrow.value ? 64 : 96));

    const vars = computed(() => ({
      ...colourVars(props.project),
      "--p-grad": gradientOf(props.project, 135),
      "--p-grad-v": gradientOf(props.project, 180),
    }));

    /* Images are fetched on first open and then kept, so a close does not
       tear the content out from under the collapse animation. */
    const loaded = ref(props.open);
    const rootEl = ref(null);
    const headingEl = ref(null);
    const t4MarkEl = ref(null);
    let stopDepth = null;

    const syncDepth = () => {
      if (t4MarkEl.value && !stopDepth) {
        stopDepth = registerLayer(t4MarkEl.value, { k: DEPTH.BACK });
      } else if (!t4MarkEl.value && stopDepth) {
        stopDepth();
        stopDepth = null;
      }
    };

    onMounted(() => {
      if (loaded.value) nextTick(syncDepth);
    });

    watch(
      () => props.open,
      (isOpen) => {
        if (isOpen) {
          loaded.value = true;
        } else if (rootEl.value && rootEl.value.contains(document.activeElement)) {
          /* Collapsing puts this whole subtree behind `inert`, so the element
             the reader is standing on stops being focusable and focus falls
             to <body> — the next Tab restarts at the top of the page. Hand it
             back to the row that controls this region instead. Done
             synchronously, before any nextTick, so a click on a *different*
             row still wins: that row's dossier focuses its own heading after
             this runs. */
          const trigger = document.querySelector(`[aria-controls="${props.id}"]`);
          if (trigger) trigger.focus({ preventScroll: true });
        }
        nextTick(() => {
          syncDepth();
          // preventScroll: the whole point of this pattern is that the row
          // does not move, so focusing must not scroll it either.
          if (isOpen && headingEl.value) headingEl.value.focus({ preventScroll: true });
        });
      }
    );

    onBeforeUnmount(() => {
      if (stopDepth) stopDepth();
    });

    return {
      title,
      description,
      caption,
      markAlt,
      specs,
      stack,
      liveHref,
      linkLabel,
      tier,
      caps,
      shts,
      highlights,
      altFor,
      markLarge,
      markPanel,
      vars,
      loaded,
      rootEl,
      headingEl,
      t4MarkEl,
      url: assetUrl,
    };
  },
};
</script>

<style scoped>
.dossier {
  display: grid;
  grid-template-rows: 0fr;
}
/* LedgerSection puts `.ledger-item { transition: opacity … }` on this same
   root element (a parent's scoped rule reaches a child's root node), at
   identical specificity. A `transition` shorthand there would silently
   cancel the expansion depending only on stylesheet order — the doubled
   class puts this rule out of reach, and both properties are declared here
   so the sibling dim still animates. */
.dossier.dossier {
  transition: grid-template-rows var(--d-4) var(--ease-out),
    opacity var(--d-2) var(--ease-out);
}
.dossier.is-open {
  grid-template-rows: 1fr;
}
.d-clip {
  min-height: 0;
  overflow: hidden;
}

.d-inner {
  position: relative;
  padding: var(--s-5) 0 var(--s-8) var(--s-5);
}
/* Colour channel 3: a 1px rule in the project's own gradient, running the
   full height of the open dossier. */
.d-inner::before {
  content: "";
  position: absolute;
  inset: 0 auto 0 0;
  width: 1px;
  background: var(--p-grad-v);
}

.d-heading {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  white-space: nowrap;
}
/* Focused programmatically, never by tabbing, and it has no visible box for
   a ring to sit on. */
.d-heading:focus {
  outline: none;
}
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  white-space: nowrap;
}

.d-content {
  opacity: 0;
  transition: opacity var(--d-3) var(--ease-out);
}
.is-open .d-content {
  opacity: 1;
  transition-delay: 120ms;
}

.d-cols {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 340px;
  gap: var(--s-7);
  align-items: start;
}
.d-desc {
  max-width: 62ch;
  margin: 0;
  font-size: var(--t-body);
  line-height: 1.62;
  color: var(--text);
  text-wrap: pretty;
}

.d-spec {
  display: grid;
  grid-template-columns: 96px minmax(0, 1fr);
  margin: 0;
}
.d-spec dt,
.d-spec dd {
  margin: 0;
  padding: 11px 0;
  border-top: var(--hair-w) solid var(--hairline);
}
.d-spec dt {
  padding-top: 13px; /* the 10.5px label sits optically level with the value */
}
.d-spec dd {
  font-size: var(--m-value);
  font-weight: 500;
  color: var(--ink);
  overflow-wrap: anywhere;
}
.d-link {
  color: var(--accent);
  text-decoration: none;
  border-bottom: var(--hair-w) solid transparent;
  transition: border-color var(--d-1) linear;
}
.d-link:hover {
  border-bottom-color: var(--accent);
}

.d-evidence {
  margin-top: var(--s-7);
}

/* T1 — the capture, at 16:10, no frame and no device chrome. */
.t1 {
  margin: 0 0 var(--s-5);
}
.t1:last-child {
  margin-bottom: 0;
}
.t1-plate {
  display: block;
  width: 100%;
  height: auto;
  aspect-ratio: 16 / 10;
  object-fit: cover;
  border-radius: var(--r-lg);
  box-shadow: var(--sh-plate);
}

/* T2 — native aspect inside a uniform 300 × 220 mat, so portrait and
   landscape captures share one rhythm and the strip snaps evenly. */
.t2 {
  display: flex;
  gap: var(--s-4);
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  padding-bottom: var(--s-2);
}
.t2-shot {
  flex: 0 0 auto;
  margin: 0;
  padding: 20px;
  border-radius: var(--r-md);
  background: color-mix(in oklab, var(--p-from) 8%, var(--ground));
  scroll-snap-align: start;
}
.t2-shot img {
  display: block;
  width: 300px;
  height: 220px;
  object-fit: contain;
}
.t2:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 4px;
}

/* T3 — the mark at full size on a bloom of its own colour. The ground
   shadow is a gradient element, because filter: blur() is banned site-wide. */
.t3 {
  position: relative;
  display: grid;
  place-items: center;
  padding: var(--s-8) 0;
}
.t3-bloom {
  position: absolute;
  width: 520px;
  height: 520px;
  max-width: 100%;
  border-radius: 50%;
  background: radial-gradient(circle at 50% 50%, var(--p-from) 0%, transparent 70%);
  opacity: 0.22;
  pointer-events: none;
}
.t3-stack {
  position: relative;
  display: grid;
  justify-items: center;
}
.t3-shadow {
  width: 300px;
  max-width: 100%;
  height: 48px;
  margin-top: -6px;
  background: radial-gradient(
    ellipse 46% 46% at 50% 40%,
    rgba(29, 29, 31, 0.2),
    rgba(29, 29, 31, 0) 72%
  );
}

/* T4 — the project's own gradient carrying its own stack. Colour channel 2. */
.t4 {
  display: flex;
  align-items: center;
  gap: var(--s-7);
  height: 260px;
  padding: 0 48px;
  border-radius: var(--r-lg);
  background: var(--p-grad);
  overflow: hidden;
}
.t4-mark {
  --travel-y: calc(
    (var(--p, 0.5) - 0.5) * (1 - var(--k, 1)) * var(--travel) * var(--depth-scale)
  );
  transform: translate3d(var(--mx, 0px), var(--travel-y), 0) scale(var(--s, 0.97));
  opacity: 0.86; /* BACK layer: k drives travel, scale and opacity together */
  flex-shrink: 0;
}
.t4-tech {
  margin: 0;
  font-size: 28px;
  font-weight: 600;
  line-height: 1.28;
  letter-spacing: -0.01em;
  color: rgba(255, 255, 255, 0.88);
}

.d-caption {
  margin-top: var(--s-2);
  font-size: var(--t-xs);
  color: var(--grey);
}

.d-highlights {
  margin-top: var(--s-6);
  max-width: 62ch;
}
.d-highlights ul {
  margin: var(--s-3) 0 0;
  padding: 0;
  list-style: none;
}
.d-highlights li {
  padding: 12px 0;
  border-top: var(--hair-w) solid var(--hairline);
  font-size: var(--t-body);
  font-weight: 500;
  color: var(--ink);
}

@media (max-width: 720px) {
  .d-inner {
    padding: var(--s-4) 0 var(--s-7) var(--s-4);
  }
  .d-cols {
    grid-template-columns: minmax(0, 1fr);
    gap: var(--s-5);
  }
  .d-spec {
    grid-template-columns: 88px minmax(0, 1fr);
  }
  .t4 {
    height: 200px;
    gap: var(--s-5);
    padding: 0 24px;
  }
  .t4-tech {
    font-size: 20px;
  }
  .t3 {
    padding: var(--s-6) 0;
  }
}
</style>
